import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  CircularProgress,
  DialogActions,
  Stack,
  Typography,
  Box
} from "@mui/material";
import { Form, Formik } from "formik";
import TextFieldWrapper from "../FormComponents/TextFieldWrapper";
import { Cancel, Forward } from "@mui/icons-material";
import * as Yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import UserQuery from "../../stateQueries/User";
import AlertPopup from "../AlertPopup";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2)
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1)
  }
}));

export default function AddEditBoardReflectionModal({ reflection }) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: async (formData) => {
      return UserQuery.CSEQuery.addBoardReflection(formData);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries(["boardReflections"]);
        setOpen(false);
      }, 2000);
    }
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, formData }) => {
      return UserQuery.CSEQuery.updateBoardReflection(id, formData);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries(["boardReflections"]);
        setOpen(false);
      }, 2000);
    }
  });

  const handleToggleOpen = () => {
    setOpen(!open);
  };

  return (
    <div>
      {reflection ? (
        <IconButton onClick={handleToggleOpen} color="primary" size="small">
          <EditIcon />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleToggleOpen}
          sx={{
            px: 3,
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
            borderRadius: 2,
            textTransform: "none",
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-2px)",
              transition: "all 0.3s ease"
            }
          }}
        >
          Add Board Reflection
        </Button>
      )}

      {addMutation.isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={addMutation.error?.response?.data?.message || "Server Error"}
        />
      )}
      {addMutation.isSuccess && (
        <AlertPopup open={true} message={addMutation.data?.message} />
      )}

      {editMutation.isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={
            editMutation.error?.response?.data?.message || "Server Error"
          }
        />
      )}
      {editMutation.isSuccess && (
        <AlertPopup open={true} message={editMutation.data?.message} />
      )}

      <BootstrapDialog
        onClose={handleToggleOpen}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth={true}
        maxWidth="sm"
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          {reflection ? "Edit Board Reflection" : "Add Board Reflection"}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleToggleOpen}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Formik
            initialValues={{
              title: reflection?.title || "",
              period: reflection?.period || "",
              document: null
            }}
            validationSchema={Yup.object().shape({
              title: Yup.string().required("Title is required"),
              period: Yup.string().required("Period is required"),
              document: !reflection && Yup.mixed().required("Document is required")
            })}
            onSubmit={(values, { setSubmitting }) => {
              const formData = new FormData();
              formData.append("title", values.title);
              formData.append("period", values.period);

              if (values.document) {
                formData.append("document", values.document);
              }

              if (reflection) {
                editMutation.mutate({ id: reflection.id, formData });
              } else {
                addMutation.mutate(formData);
              }

              setSubmitting(false);
            }}
          >
            {({ setFieldValue, isSubmitting, values, errors, touched }) => (
              <Form>
                <Stack spacing={2}>
                  <TextFieldWrapper
                    name="title"
                    label="Title"
                    placeholder="Enter reflection title"
                    required
                  />

                  <TextFieldWrapper
                    name="period"
                    label="Period"
                    placeholder="e.g., 2025-01 or Q1 2025"
                    required
                  />

                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Document{" "}
                      {!reflection && <span style={{ color: "red" }}>*</span>}
                    </Typography>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        setFieldValue("document", file || null);
                      }}
                      style={{
                        padding: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        width: "100%",
                        boxSizing: "border-box"
                      }}
                    />
                    {reflection?.documentURL && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        Current: {reflection.documentURL}
                      </Typography>
                    )}
                    {errors.document && touched.document && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.document}
                      </Typography>
                    )}
                  </Box>

                  <DialogActions sx={{ px: 0, pb: 0 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleToggleOpen}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Forward />}
                      disabled={
                        isSubmitting ||
                        addMutation.isLoading ||
                        editMutation.isLoading
                      }
                    >
                      {isSubmitting ||
                      addMutation.isLoading ||
                      editMutation.isLoading ? (
                        <CircularProgress size={24} />
                      ) : reflection ? (
                        "Update"
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </DialogActions>
                </Stack>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </BootstrapDialog>
    </div>
  );
}
